import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getUsers } from "../Auth";
import DateInput from "../components/datePicker/DateInput";

const credentialsList = [
  "Professional License",
  "CPR",
  "Driver's License",
  "Car Insurance",
  "OIG",
  "Liability Insurance",
  "Performance Evaluation",
  "Competency",
  "TB Test",
  "TB Form",
  "Flu Shot",
];

// Helper function to format date for input field (YYYY-MM-DD)
const formatDateForInput = (date) => {
  if (!date || date === null || date === undefined) return "";
  if (typeof date === "string" && date.trim() === "") return "";
  
  try {
    let dateObj;
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date.trim())) {
        return date.trim();
      }
      dateObj = new Date(date);
    } else {
      return "";
    }
    
    if (!isNaN(dateObj.getTime())) {
      return dateObj.toISOString().split('T')[0];
    }
  } catch (e) {
    console.error("Date formatting error:", e);
  }
  return "";
};

const ExpiredCredentials = () => {
  const navigate = useNavigate();
  const [expiredList, setExpiredList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const loadExpired = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Use optimized endpoint that excludes file data
      const users = await getUsers(true);
      const results = [];

      users.forEach((u) => {
        credentialsList.forEach((cred) => {
          const credData = u.credentials?.[cred] || {};
          const date = credData.date;
          if (date && new Date(date) < new Date()) {
            results.push({
              userId: u._id,
              email: u.email,
              name: `${u.firstName} ${u.lastName}`,
              credential: cred,
              expiry: formatDateForInput(date), // Format date for input
              notes: credData.notes || "",
            });
          }
        });
      });

      setExpiredList(results);
    } catch (err) {
      setError(err.message || "Failed to load credentials");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExpired();
  }, [loadExpired]);

  const handleUpload = (userId) => {
    navigate(`/upload-documents/${userId}`);
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const sortedList = [...expiredList].sort((a, b) => {
    if (!sortKey) return 0;
    let valA, valB;
    
    if (sortKey === "expiry") {
      valA = a[sortKey] ? new Date(a[sortKey]).getTime() : 0;
      valB = b[sortKey] ? new Date(b[sortKey]).getTime() : 0;
    } else {
      valA = (a[sortKey] || "").toString().toLowerCase();
      valB = (b[sortKey] || "").toString().toLowerCase();
    }
    
    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <div className="p-6 relative min-h-screen">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-red-700">Expired Credentials</h1>
      </div>

      {error && (
        <div className="mb-4 rounded bg-red-100 text-red-800 px-4 py-2">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-600">Loading credentials...</div>
      ) : (
      <table className="w-full bg-white shadow-lg rounded-lg overflow-hidden">
        <thead className="bg-gray-100 text-gray-700">
          <tr>
            {["name", "credential", "expiry", "notes"].map((key) => (
              <th
                key={key}
                className="p-3 text-left cursor-pointer hover:bg-gray-200"
                onClick={() => toggleSort(key)}
              >
                {key === "name" ? "Employee" : key === "expiry" ? "Expired On" : key.charAt(0).toUpperCase() + key.slice(1)}{" "}
                {sortKey === key ? (sortOrder === "asc" ? "▲" : "▼") : ""}
              </th>
            ))}
            <th className="p-3 text-left">Action</th>
          </tr>
        </thead>
        <tbody>
          {expiredList.length === 0 ? (
            <tr>
              <td colSpan="5" className="p-4 text-center text-gray-500">
                No expired credentials found.
              </td>
            </tr>
          ) : (
            sortedList.map((item, idx) => (
              <tr key={idx} className="border-b hover:bg-gray-50">
                <td className="p-3">{item.name}</td>
                <td className="p-3">{item.credential}</td>
                <td className="p-3">
                  <DateInput
                    className="border p-2 w-full rounded text-red-600 bg-gray-50"
                    value={item.expiry || ""}
                    readOnly
                  />
                </td>
                <td className="p-3">
                  <input
                    type="text"
                    className="border p-2 w-full rounded bg-gray-50"
                    placeholder="Notes"
                    value={item.notes || ""}
                    readOnly
                  />
                </td>
                <td className="p-3">
                  <button
                    onClick={() => handleUpload(item.userId)}
                    className="bg-green-100 text-green-800 px-3 py-1 rounded hover:bg-green-200 font-semibold"
                  >
                    Upload
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      )}
    </div>
  );
};

export default ExpiredCredentials;
